import type { Store } from '@reduxjs/toolkit';
import { hasPreTradeRelevantChanges } from '@src/components/PreTrade/hooks/useOrderRequestChanged/hasPreTradeRelevantChanges';
import { resumeTracking } from '@src/components/PreTrade/redux/preTradeSlice';
import type { AppState } from '@src/providers/AppStateProvider/types';
import type { FullOrderRequest, ModifyOrderRequest } from '@src/providers/orders.types';
import {
  DateField,
  DecisionStatusEnum,
  ErrorActionEnum,
  type ErrorSubscriptionResponse,
  ExecTypeEnum,
  type ExecutionReport,
  formattedDateForSubscription,
  getTypedKeys,
  isOption,
  type IStrategyParameters,
  type IWebSocketClient,
  logger,
  type MarketDataSnapshot,
  ModeEnum,
  NEW_ORDER_SINGLE,
  NotificationVariants,
  ORDER,
  Order,
  ORDER_MODIFY_REQUEST,
  type OrderStrategy,
  OrdStatusEnum,
  OrdTypeEnum,
  ParameterTypeEnum,
  PricingMode,
  ReduceFirstEnum,
  ReduceOnlyEnum,
  type RequestStream,
  type Security,
  type SubscriptionResponse,
  TimeInForceEnum,
  type ToastProps,
  UnifiedLiquidityEnum,
} from '@talos/kyoko';
import type { RefObject } from 'react';
import { Observable, type Subscription, timeout } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { ParameterKeysEnum } from '../../../tokens/orderStrategy';
import { getExpectedFillPriceAndSize } from '../../../utils/getExpectedFillPriceAndSize';
import { applyEndTimeDurationToParams } from '../../../utils/order';
import { addOtherOrderCardsData } from '../Cards/ActiveOrderCardsSlice';
import { getReduceFirstSupport } from '../useGetReduceFirstSupport';
import { getReduceOnlySupport } from '../useGetReduceOnlySupport';
import { getOrdType } from '../utils';
import { setIsLoading } from './OrderSlice';
import type { OrderFormState, OrderState } from './types';
import { shouldSendDurationOnModify } from './utils';

export const EXEC_REPORT_RESPONSE_TIMEOUT_MS = 10_000;

interface OrderSubscription {
  key?: string;
  subscription: Subscription;
}

interface ExecutionReportRequest extends RequestStream {
  OrderID: string;
  StartDate?: string;
  SendMarkets: boolean;
  HideAPICalls: boolean;
  Throttle: string;
  CoalesceExecs: ExecTypeEnum[];
}

function getOrderSubscriptionKey(type: 'Order' | 'ExecutionReport', id: string): string {
  return `${type}-${id}`;
}

/**
 * Handles websocket communication around Principal Orders
 * - Places, modifies and cancels orders
 * - Does transformation of UI form model to order placement calls
 * - Triggers state changes and showing errors when hearing acks on orders
 */
export class OrderService {
  private subscriptions: OrderSubscription[] = [];
  private timeoutId: ReturnType<typeof setTimeout> | undefined;
  private marketDataSnapshotRef?: RefObject<MarketDataSnapshot | undefined>;
  private showedTimeoutError = false;

  constructor(
    private store: Store<AppState>,
    private webSocketClient: IWebSocketClient<unknown>,
    private executionReportSubscription: Observable<SubscriptionResponse<ExecutionReport>>,
    private recentOrdersSubscription: Observable<Order[]>,
    private orgApiEndpoint: string,
    private addToast: (props: ToastProps) => void,
    private addGlobalToast: (props: ToastProps) => void,
    private handleOrderConfirmed: (order: ExecutionReport | Order | null) => void
  ) {}

  public requestOrder(useTradeAllocations: boolean): void {
    const request = this.generateRequest(useTradeAllocations);
    if (!request) {
      return;
    }
    this.debug('NewOrderSingle', request);
    this.store.dispatch(setIsLoading(true));
    this.showedTimeoutError = false;
    this.startTimeoutCounter(request);

    this.observeExecReport(request);

    const cleanup = this.webSocketClient.registerPublication(
      {
        type: NEW_ORDER_SINGLE,
        data: [request],
      },
      err => this.handleNewOrderSingleError(err)
    );

    // stop listening for errors 10 seconds after publication
    setTimeout(() => cleanup(), 10_000);
  }

  public setLatestMarketDataSnapshotRef(ref: RefObject<MarketDataSnapshot | undefined>) {
    this.marketDataSnapshotRef = ref;
  }

  public updateOrder(useTradeAllocations: boolean): void {
    const request = this.generateRequest(
      useTradeAllocations,
      this.state.orderBeingModified?.OrderID
    ) as ModifyOrderRequest;
    if (!request || !request.OrderID) {
      logger.error(new Error('Missing required fields for order modification'), {
        extra: { request: request, requestOrderID: request.OrderID, orderBeingModified: this.state.orderBeingModified },
      });
      return;
    }
    this.debug('OrderCancelReplace', request);
    this.store.dispatch(setIsLoading(true));
    this.startTimeoutCounter(request);

    this.observeExecReport(request);

    const cleanup = this.webSocketClient.registerPublication(
      {
        type: ORDER_MODIFY_REQUEST,
        data: [request],
      },
      err => this.handleNewOrderSingleError(err)
    );

    // stop listening for errors 10 seconds after publication
    setTimeout(() => cleanup(), 10_000);
  }

  public getOrderRequestForPreTrade(useTradeAllocations: boolean): FullOrderRequest | undefined {
    const request = this.generateRequest(useTradeAllocations) as FullOrderRequest | undefined;
    if (request) {
      // Strip submission-only tracking fields injected by generateRequest
      delete request.PreTradeSessionID;
      delete request.PreTradeRequestID;
    }
    return request;
  }

  /**
   * If orderStartTime provided we will hit the index on the backend and get the lookup faster.
   *
   * TODO we should switch to just using the order and not rely on loading the exec reports of the order. We only care
   * about the last exec report anyways and that is what the Order effectively is.
   */
  public requestOrderInfo(orderID: string, orderStartTime?: string): void {
    const subscription = new Observable<Order>(observer => {
      const orderRequest: ExecutionReportRequest = {
        tag: 'ORDER_SERVICE_EXEC_REPORT',
        name: ORDER,
        OrderID: orderID,
        SendMarkets: true,
        HideAPICalls: true,
        Throttle: '1s',
        CoalesceExecs: [ExecTypeEnum.Restated, ExecTypeEnum.Trade],
      };

      if (orderStartTime) {
        orderRequest.StartDate = orderStartTime;
      }

      const address = uuid();
      this.webSocketClient.registerSubscription(address, [orderRequest], (err, json) => {
        if (json?.data?.length) {
          const order = json.data[json.data.length - 1] as Order;
          observer.next(order);
        }
      });

      return () => this.webSocketClient.unregisterSubscription(address);
    })
      .pipe(timeout({ first: 10000 }))
      .subscribe(
        order => {
          this.store.dispatch(
            addOtherOrderCardsData({
              order,
              submitTime: order.SubmitTime,
            })
          );
        },
        error => {
          // If we could not find the order we are modifying then freak out and switch to the cards
          logger.error(new Error(`Unable to find order for modification`), {
            extra: { orderID },
          });
          this.addGlobalToast({
            id: uuid(),
            text: `Unable to find order ${orderID} for modification. This is expected for API orders`,
            variant: NotificationVariants.Warning,
            timeout: 5000,
          });
          this.handleOrderConfirmed(null);
        }
      );

    this.subscriptions.push({
      subscription,
    });
  }

  private handleNewOrderSingleError(error: ErrorSubscriptionResponse) {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.store.dispatch(setIsLoading(false));

    logger.error(new Error(`NewOrderSingle publication returned an error in OrderService`), {
      extra: { error },
    });

    this.addToast({
      id: uuid(),
      text: `Error: ${error.error?.msg}`,
      variant: NotificationVariants.Negative,
      timeout: 5000,
      dismissable: true,
    });
  }

  private startTimeoutCounter(request: FullOrderRequest | ModifyOrderRequest) {
    this.timeoutId = setTimeout(() => {
      logger.error(
        new Error(`"The request is taking longer then expected" error was shown to the user in Order form`),
        {
          extra: { request },
        }
      );

      this.addToast({
        id: uuid(),
        text: 'The request is taking longer then expected',
        variant: NotificationVariants.Warning,
        timeout: 5000,
        dismissable: true,
      });
      this.showedTimeoutError = true;
    }, EXEC_REPORT_RESPONSE_TIMEOUT_MS);
  }

  private isModifyOrderRequest(request: FullOrderRequest | ModifyOrderRequest): request is ModifyOrderRequest {
    return (request as ModifyOrderRequest).OrderID !== undefined;
  }

  private generateRequest(
    useTradeAllocations: boolean,
    orderID?: string
  ): FullOrderRequest | ModifyOrderRequest | undefined {
    const { symbolField, sideField, orderCurrencyField, quantityField, strategyField } = this.formState;
    if (!symbolField.value || !sideField.value || !quantityField.value || !strategyField.value) {
      logger.error(new Error('Missing required fields for order generation'), {
        extra: {
          symbol: symbolField.value,
          side: sideField.value,
          quantity: quantityField.value,
          strategy: strategyField.value,
          orderID: orderID,
        },
      });
      return;
    }

    // Validate that the selected currency is in the list of available currencies
    if (
      orderCurrencyField.value !== undefined &&
      !orderCurrencyField.availableItems.includes(orderCurrencyField.value)
    ) {
      const currencyLabel = orderCurrencyField.value === '' ? 'Contracts' : orderCurrencyField.value;
      const allowedLabels = orderCurrencyField.availableItems.map(c => (c === '' ? 'Contracts' : c)).join(', ');

      logger.error(new Error('Currency validation failed'), {
        extra: {
          selectedCurrency: orderCurrencyField.value,
          availableCurrencies: orderCurrencyField.availableItems,
          symbol: symbolField.value.Symbol,
          side: sideField.value,
          strategy: strategyField.value.Name,
          orderID: orderID,
        },
      });

      this.addToast({
        id: uuid(),
        text: `Currency "${currencyLabel}" is not valid. Allowed currencies: ${allowedLabels}`,
        variant: NotificationVariants.Negative,
        timeout: 5000,
        dismissable: true,
      });

      return;
    }

    const clOrdId = uuid();

    const ordType = getOrdType(
      this.state.referenceData.settings.showAllInPrices,
      symbolField.value,
      strategyField.value
    );

    const { expectedFillQty, expectedFillPrice } = getExpectedFillPriceAndSize(
      symbolField.value,
      orderCurrencyField.value!,
      this.marketDataSnapshotRef?.current != null ? this.marketDataSnapshotRef.current : undefined,
      sideField.value,
      quantityField.value
    );

    const request: FullOrderRequest | ModifyOrderRequest = {
      OrderID: orderID,
      Symbol: symbolField.value.Symbol,
      ClOrdID: clOrdId,
      Side: sideField.value,
      Currency: orderCurrencyField.value,
      OrderQty: quantityField.value,
      OrdType: ordType,
      Strategy: strategyField.value.Name,
      Parameters: this.getParameters(),
      TimeInForce: TimeInForceEnum.GoodTillCancel,
      Price: ordType !== OrdTypeEnum.Market ? this.formState.priceField.value : undefined,
      Markets: this.formState.orderMarketsField.selections,
      TransactTime: formattedDateForSubscription(new Date()),
      Group: this.formState.groupField?.value || '',
      Comments: this.formState.commentsField?.value || '',
    };

    // Resubmit/modify of order when groups are disabled will remove the group from the order.
    if (!this.state.referenceData.settings.enableGroups) {
      request.Group = '';
    }

    if (this.formState.priceModeField.value !== PricingMode.Default) {
      request.PricingMode = this.formState.priceModeField.value;
    }

    if (!orderID && expectedFillPrice && expectedFillQty) {
      (request as FullOrderRequest).ExpectedFillPrice = expectedFillPrice;
      (request as FullOrderRequest).ExpectedFillQty = expectedFillQty;
    }

    if (this.formState.legParams.value || this.formState.ulLegParamsField.value) {
      request.LegParams = [
        ...this.formState.legParams.getLegParamsForOrderSubmission(),
        ...this.formState.ulLegParamsField.getLegParamsForOrderSubmission(),
      ];
    }

    if (useTradeAllocations && this.formState.allocations?.length) {
      const allocations = this.formState.allocations.map(a => ({
        SubAccount: a.subAccountField.value!,
        Value: a.valueField.value!,
      }));
      request.Allocation = {
        ValueType: this.formState.allocationValueTypeField.value!,
        Allocations: allocations,
      };
    } else if (this.formState.allocations?.length === 1) {
      request.SubAccount = this.formState.allocations[0].subAccountField.value;
    }

    if (!this.isModifyOrderRequest(request) && this.formState.stagedOrderField?.value) {
      request.InitialDecisionStatus = DecisionStatusEnum.Staged;
    }

    // Attach pre-trade tracking fields to new orders only (UI-6943).
    // Skip when tracking is suspended (e.g. non-PreTrade resubmit, UI-7246).
    if (!orderID) {
      const preTradeState = this.store.getState().preTrade;

      if (preTradeState?.activeSessionId && !preTradeState.suspendTracking) {
        (request as FullOrderRequest).PreTradeSessionID = preTradeState.activeSessionId;

        const referenceMatchesSession =
          preTradeState.referenceSessionId != null &&
          preTradeState.referenceSessionId === preTradeState.activeSessionId;

        if (referenceMatchesSession && preTradeState.preTradeRequestId && preTradeState.referenceNOS) {
          if (!hasPreTradeRelevantChanges(request, preTradeState.referenceNOS)) {
            (request as FullOrderRequest).PreTradeRequestID = preTradeState.preTradeRequestId;
          }
        }
      }

      // Consume the one-shot suspension so subsequent orders resume tracking.
      if (preTradeState?.suspendTracking) {
        this.store.dispatch(resumeTracking());
      }
    }

    return request;
  }

  private displayFailedToast = (order: ExecutionReport | Order): void => {
    const verb = order.OrdStatus === OrdStatusEnum.Rejected ? 'rejected' : 'cancelled';
    this.addToast({
      text: order.Text || `Order ${verb} with unknown reason. Please contact Talos support.`,
      variant: NotificationVariants.Warning,
      timeout: 5000,
    });
  };

  private observeExecReport({ ClOrdID, OrderID }: { ClOrdID: string; OrderID?: string }): void {
    // We observe the exec reports sub as if our request ends in an error (like modifying an order that went terminal)
    // we will only get an exec report for the ClOrdID and no order will be created.
    const execSub = this.executionReportSubscription.subscribe(this.processExecReportOrOrderForClOrdId(ClOrdID));
    this.subscriptions.push({ key: getOrderSubscriptionKey('ExecutionReport', ClOrdID), subscription: execSub });

    // We also observe the orders sub as if we have a disconnect while the order is in flight we will not get the missing
    // exec reports when that subscription recovers
    const orderSub = this.recentOrdersSubscription.subscribe(this.processExecReportOrOrderForClOrdId(ClOrdID, OrderID));
    this.subscriptions.push({ key: getOrderSubscriptionKey('Order', ClOrdID), subscription: orderSub });
  }

  private processExecReportOrOrderForClOrdId(clOrdID: string, orderID?: string) {
    return (execReportResponseOrOrderArray: SubscriptionResponse<ExecutionReport, string> | Order[]) => {
      let execReportOrOrder: Order | ExecutionReport | undefined = undefined;
      if (Array.isArray(execReportResponseOrOrderArray)) {
        execReportOrOrder = execReportResponseOrOrderArray.find(order => order.ClOrdID === clOrdID);
        this.debug('orderReceived', execReportOrOrder);
      } else {
        execReportOrOrder = execReportResponseOrOrderArray.data.findLast(report => report.ClOrdID === clOrdID);
        this.debug('executionReportReceived', execReportOrOrder);
      }

      if (!execReportOrOrder) {
        // This is not the order/execReport you are looking for
        return;
      }

      this.store.dispatch(setIsLoading(false));

      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }

      if (this.showedTimeoutError) {
        logger.info('Recovered after showing timeout error to user', {
          extra: { clOrdId: clOrdID },
        });
        this.showedTimeoutError = false;
      }

      switch (execReportOrOrder.OrdStatus) {
        case OrdStatusEnum.Rejected:
        case OrdStatusEnum.Canceled:
          this.displayFailedToast(execReportOrOrder);
          this.unsubscribe(getOrderSubscriptionKey('ExecutionReport', clOrdID));
          this.unsubscribe(getOrderSubscriptionKey('Order', clOrdID));
          break;
        default:
          if (execReportOrOrder.ExecType === ExecTypeEnum.ReplaceRejected) {
            this.displayFailedToast(execReportOrOrder);
            this.unsubscribe(getOrderSubscriptionKey('ExecutionReport', clOrdID));
            this.unsubscribe(getOrderSubscriptionKey('Order', clOrdID));
          } else if (execReportOrOrder instanceof Order) {
            // Pass along the order so we can show a feedback card in a popout order form
            this.handleOrderConfirmed(execReportOrOrder);
            // Unsubscribe from the exec report and order subscriptions as we don't need them anymore for this order
            this.unsubscribe(getOrderSubscriptionKey('ExecutionReport', clOrdID));
            this.unsubscribe(getOrderSubscriptionKey('Order', clOrdID));
          } else {
            // Pass along exec reports to open the cards view, or the feedback card with a spinner
            this.handleOrderConfirmed(execReportOrOrder);
          }
      }
    };
  }

  private unsubscribe(key: string) {
    this.subscriptions.forEach(({ key: k, subscription }) => {
      if (k === key) {
        subscription.unsubscribe();
      }
    });
    this.subscriptions = this.subscriptions.filter(({ key: k }) => k !== key);
  }

  private getParameters(): { [key: string]: any } {
    const security = this.formState.symbolField.value;
    const enableDurationSubmission = this.store.getState().referenceData.settings.enableDurationSubmission;
    const isStagedOrder = Boolean(this.formState.stagedOrderField?.value);
    const decisionStatus = this.state.orderBeingModified?.DecisionStatus;
    let shouldSendDuration: boolean;

    if (ParameterKeysEnum.Duration in this.formState.parameters) {
      // Strategy has a dedicated Duration field in the form
      shouldSendDuration = false;
    } else if (this.state.orderBeingModified) {
      // Modify: depends on DecisionStatus
      shouldSendDuration = shouldSendDurationOnModify({
        decisionStatus,
        enableDurationSubmission,
      });
    } else {
      // New order: only send Duration when enabled by feature flag or when staged
      shouldSendDuration = enableDurationSubmission || isStagedOrder;
    }
    const params = getTypedKeys(this.formState.parameters).reduce(
      (acc, key) => {
        // We need to send explicit null for Dates if user cleared a previously set value; if we don't send it or send undefined the previous value will be kept
        const shouldIgnoreDate =
          this.formState.parameters[key] instanceof DateField
            ? // TODO: key is not guaranteed to be a key of Partial<IStrategyParameters>
              !this.state.orderBeingModified?.Parameters?.[key as keyof Partial<IStrategyParameters>]
            : true;

        const shouldIgnore = !this.formState.parameters[key].hasValue && shouldIgnoreDate;
        const isUnifiedLiquidityEnabled =
          this.formState.parameters[ParameterTypeEnum.UnifiedLiquidity]?.value === UnifiedLiquidityEnum.Enabled;

        // Do not send some parameters if they are not supported
        switch (key) {
          case ParameterTypeEnum.ReduceFirst:
            if (
              this.formState.parameters[key].value === ReduceFirstEnum.Enabled &&
              !getReduceFirstSupport(security, isUnifiedLiquidityEnabled)
            ) {
              return acc;
            }
            break;

          case ParameterTypeEnum.ReduceOnly:
            if (
              this.formState.parameters[key].value === ReduceOnlyEnum.Enabled &&
              !getReduceOnlySupport(
                security,
                this.formState.strategyField.value,
                this.formState.orderMarketsField.selections,
                isUnifiedLiquidityEnabled,
                this.store.getState().referenceData.marketAccounts.marketAccountsByName,
                this.store.getState().referenceData.markets.marketsByName
              )
            ) {
              return acc;
            }
            break;
        }
        if (shouldIgnore) {
          return acc;
        }

        const paramDef = this.formState.strategyField.value?.Parameters.find(p => p.Name === key);
        // Primary parameters with payloadKey are UI-only toggles, never emitted directly
        if (paramDef?.isPrimary && paramDef?.payloadKey) {
          return acc;
        }
        // Symbol, Aggregation, and UnifiedLiquidity parameters that belong to a section with a
        // payloadKey primary param are nested inside that payloadKey object (handled below). Skip
        // them here so they are not also emitted at the top level of Parameters.
        // HedgeStrategy is intentionally excluded — it is emitted at the top level (handled below).
        if (
          paramDef !== undefined &&
          (paramDef.Type === ParameterTypeEnum.Symbol ||
            paramDef.Type === ParameterTypeEnum.Aggregation ||
            paramDef.Type === ParameterTypeEnum.UnifiedLiquidity) &&
          paramDef.section != null &&
          this.formState.strategyField.value?.getPrimaryParameterForSection(paramDef.section)?.payloadKey != null
        ) {
          return acc;
        }
        // HedgeStrategy parameters serialize the OrderStrategy object to its name string
        if (paramDef?.Type === ParameterTypeEnum.HedgeStrategy) {
          const stratValue = this.formState.parameters[key].value as OrderStrategy | undefined;
          acc[key] = stratValue?.Name ?? null;
          return acc;
        }

        acc[key] = this.formState.parameters[key].value ?? null;
        return acc;
      },
      {} as Record<string, any>
    );
    // Force this parameter on all orders
    params['ErrorAction'] = ErrorActionEnum.Pause;

    // Handle sections with payloadKey: nest Symbol parameters under the specified key
    const strategy = this.formState.strategyField.value;

    // New parameter-driven DDH approach: nest Symbol parameters under payloadKey.
    // Only runs when the current strategy has the DynamicDeltaHedgingEnabled parameter.
    // Uses new parameter-driven approach (payloadKey nesting + HedgeStrategy type emit) when true,
    // legacy approach (isDDHEnabled / ddhStrategyField / ddhSymbolField) when false.
    if (strategy?.hasParameterDrivenDDH) {
      strategy.Parameters.filter(p => p.isPrimary && p.payloadKey).forEach(primaryParam => {
        const primaryField = this.formState.parameters[primaryParam.Name];
        const isSectionEnabled = primaryField?.value === ModeEnum.Enabled;
        if (isSectionEnabled) {
          const nestedParams: Record<string, any> = {};
          strategy
            .getSectionParameters(primaryParam.section!)
            .filter(
              p =>
                !p.isPrimary &&
                (p.Type === ParameterTypeEnum.Symbol ||
                  p.Type === ParameterTypeEnum.Aggregation ||
                  p.Type === ParameterTypeEnum.UnifiedLiquidity)
            )
            .forEach(childParam => {
              const childField = this.formState.parameters[childParam.Name];
              if (childField?.hasValue) {
                if (childParam.Type === ParameterTypeEnum.Symbol) {
                  nestedParams[childParam.Name] = (childField.value as Security)?.Symbol ?? '';
                } else if (childParam.Type === ParameterTypeEnum.UnifiedLiquidity) {
                  // Only nest UnifiedLiquidity when explicitly Enabled; omit when Disabled (backend default).
                  // Key is always 'UnifiedLiquidity' regardless of the parameter's internal Name.
                  if (childField.value === UnifiedLiquidityEnum.Enabled) {
                    nestedParams[ParameterTypeEnum.UnifiedLiquidity] = UnifiedLiquidityEnum.Enabled;
                  }
                } else {
                  // Aggregation: value is the aggregation name string
                  nestedParams[childParam.Name] = childField.value ?? '';
                }
              }
            });
          params[primaryParam.payloadKey!] = nestedParams;
        }
      });
    }

    // Legacy DDH approach: only runs when the current strategy does NOT have the parameter-driven
    // DynamicDeltaHedgingEnabled parameter. Uses the explicit isDDHEnabled form state set by
    // the hardcoded DynamicDeltaHedge UI component. Remove once all strategies have
    // DynamicDeltaHedgingEnabled as a strategy parameter.
    if (!strategy?.hasParameterDrivenDDH && isOption(security) && this.formState.isDDHEnabled.value) {
      params['DynamicDeltaHedgeStrategy'] = this.formState.ddhStrategyField.value?.Name ?? '';
      const ddhStrategyParams: Record<string, string> = {
        DynamicDeltaHedgeSymbol: this.formState.ddhSymbolField.value?.Symbol ?? '',
      };
      const aggregation = this.formState.ddhAggregationField.value;
      if (aggregation) {
        ddhStrategyParams['DynamicDeltaHedgeAggregation'] = aggregation;
      }
      const unifiedLiquidity = this.formState.ddhUnifiedLiquidityField.value;
      if (unifiedLiquidity === UnifiedLiquidityEnum.Enabled) {
        ddhStrategyParams['UnifiedLiquidity'] = unifiedLiquidity;
      }
      params['DynamicDeltaHedgeStrategyParams'] = ddhStrategyParams;
    }

    // Swap Parameters.EndTime -> Parameters.Duration when allowed; otherwise DateField already computed EndTime from duration
    if (shouldSendDuration) {
      applyEndTimeDurationToParams(this.formState.parameters[ParameterKeysEnum.EndTime], params);
    }
    return params;
  }

  private debug(key: string, value: any) {
    // dispatch an action so it's logged and can be debugged in the redux debugger but isn't actually handled by reducer and does not affect state in any ways
    this.store.dispatch({ type: `order/debug/${key}`, payload: value });
  }

  private get formState(): OrderFormState {
    return this.state.form;
  }

  private get state(): OrderState {
    return this.store.getState().order;
  }

  public destroy(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.subscriptions.forEach(({ subscription }) => subscription.unsubscribe());
  }
}
